const KLAVIYO_REVISION = '2026-04-15';

const subscribeToKlaviyoList = async ({
    publicApiKey,
    listId,
    email,
    source,
    firstName,
    dogName
}) => {
    if (!publicApiKey || !listId || !email) {
        console.error('Missing required Klaviyo subscription params', {
            publicApiKey: !!publicApiKey,
            listId,
            email
        });

        throw new Error('Missing required Klaviyo subscription params');
    }

    const profileProperties = {};

    if (dogName) {
        profileProperties.dog_name = dogName;
    }

    const attributes = {
        profile: {
            data: {
                type: 'profile',
                attributes: {
                    email,
                    ...(firstName ? { first_name: firstName } : {}),
                    ...(Object.keys(profileProperties).length
                        ? { properties: profileProperties }
                        : {}),
                    subscriptions: {
                        email: {
                            marketing: {
                                consent: 'SUBSCRIBED'
                            }
                        }
                    }
                }
            }
        }
    };

    if (source) {
        attributes.custom_source = source;
    }

    try {
        const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicApiKey)}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                    Accept: 'application/vnd.api+json',
                    revision: KLAVIYO_REVISION
                },
                body: JSON.stringify({
                    data: {
                        type: 'subscription',
                        attributes,
                        relationships: {
                            list: {
                                data: {
                                    type: 'list',
                                    id: listId
                                }
                            }
                        }
                    }
                })
            }
        );

        if (!response.ok) {
            let errorBody = null;

            try {
                errorBody = await response.json();
            } catch (jsonError) {
                console.error('Could not parse Klaviyo subscription error response', jsonError);
            }

            console.error('Klaviyo subscription error:', {
                status: response.status,
                statusText: response.statusText,
                listId,
                email,
                errorBody
            });

            throw new Error(`Klaviyo subscription failed for list ${listId}`);
        }

    } catch (klaviyoError) {
        throw klaviyoError;
    }
};

const trackKlaviyoEvent = async ({
    publicApiKey,
    email,
    eventName,
    properties = {}
}) => {

    try {
        const response = await fetch(
            `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(publicApiKey)}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                    Accept: 'application/vnd.api+json',
                    revision: KLAVIYO_REVISION
                },
                body: JSON.stringify({
                    data: {
                        type: 'event',
                        attributes: {
                            metric: {
                                data: {
                                    type: 'metric',
                                    attributes: {
                                        name: eventName
                                    }
                                }
                            },
                            profile: {
                                data: {
                                    type: 'profile',
                                    attributes: {
                                        email
                                    }
                                }
                            },
                            properties
                        }
                    }
                })
            }
        );

        if (!response.ok) {
            let errorBody = null;

            try {
                errorBody = await response.json();
            } catch (jsonError) {
                console.error('Could not parse Klaviyo event error response', jsonError);
            }

            console.error('Klaviyo event error:', {
                status: response.status,
                statusText: response.statusText,
                eventName,
                email,
                errorBody
            });

            throw new Error(`Klaviyo event failed: ${eventName}`);
        }
    } catch (klaviyoError) {
        throw klaviyoError;
    }
};

const handleKlaviyoSubmission = async (form) => {
    const KLAVIYO_PUBLIC_API_KEY = form.dataset.klaviyoKey?.trim();
    const ALL_SUBSCRIBERS_LIST_ID = form.dataset.klaviyoList?.trim();

    const success = form.querySelector('[data-success]');
    const error = form.querySelector('[data-error]');
    if (success) success.hidden = true;
    if (error) error.hidden = true;

    const contactFields = form.querySelector('[data-contact]');
    const submitButton = form.querySelector('[data-submit]');

    const source = form.dataset.klaviyoSource?.trim();
    const fields = form.elements;
    const email = fields.email?.value?.trim();
    const firstName = fields.first_name?.value?.trim();
    const dogName = fields.dog_name?.value?.trim();

    if (!email) {
        handleKlaviyoError(form);
        return;
    }

    // Checkbox-specific Freebie list IDs and event
    const eventName = form.dataset.klaviyoEventName?.trim();
    const eventProperty = form.dataset.klaviyoEventProperty?.trim();

    const selectedFreebies = Array.from(
        form.querySelectorAll('.js-klaviyo-checkbox:checked')
    ).map((checkbox) => ({
        listId: checkbox.dataset.klaviyoList?.trim(),
        value: checkbox.value?.trim()
    })).filter((freebie) => freebie.listId && freebie.value);

    const selectedFreebieListIds = selectedFreebies.map(
        (freebie) => freebie.listId
    );

    const selectedFreebieValues = selectedFreebies.map(
        (freebie) => freebie.value
    );

    const uniqueListIds = [
        ...new Set([
            ALL_SUBSCRIBERS_LIST_ID,
            ...selectedFreebieListIds
        ].filter(Boolean))
    ];

    if (!KLAVIYO_PUBLIC_API_KEY) {
        console.error('Missing Klaviyo public API key');
        handleKlaviyoError(form);
        return;
    }

    if (!uniqueListIds.length) {
        console.error('No Klaviyo list IDs found');
        handleKlaviyoError(form);
        return;
    }

    try {
        // Subscribe to All Subscribers + selected Freebie lists.
        await Promise.all(
            uniqueListIds.map((listId) =>
                subscribeToKlaviyoList({
                    publicApiKey: KLAVIYO_PUBLIC_API_KEY,
                    listId,
                    email,
                    source,
                    firstName,
                    dogName
                })
            )
        );

        // Send Klaviyo event
        if (eventName && eventProperty && selectedFreebieValues.length) {
            await trackKlaviyoEvent({
                publicApiKey: KLAVIYO_PUBLIC_API_KEY,
                email,
                eventName,
                properties: {
                    [eventProperty]: selectedFreebieValues,
                    ...(source ? { source } : {}),
                    ...(firstName ? { first_name: firstName } : {}),
                    ...(dogName ? { dog_name: dogName } : {})
                }
            });
        }

        // Store values in storage
        try {
            localStorage.setItem('klaviyo_email', email);

            if (firstName) {
                localStorage.setItem('klaviyo_first_name', firstName);
            }

            if (dogName) {
                localStorage.setItem('klaviyo_dog_name', dogName);
            }
        } catch (storageError) {
            console.warn('Could not save Klaviyo form values:', storageError);
        }

        form.reset();
        if (success) success.hidden = false;
        if (contactFields) contactFields.style.display = 'none';
        if (submitButton) {
            submitButton.style.display = 'none';
            submitButton.blur();
        }

    } catch (error) {
        console.error('Klaviyo form submission failed:', {
            error,
            email,
            selectedFreebieListIds,
            uniqueListIds
        });

        handleKlaviyoError(form);
    }
};

const resetSubmitButton = (form) => {
    const submitButton = form.querySelector('[data-submit]');
    if (submitButton) {
        submitButton.blur();
        submitButton.classList.add('is-submitting');

        setTimeout(() => {
            submitButton.classList.remove('is-submitting');
        }, 5000);
    }
    form.focus();
};

const handleKlaviyoError = (form) => {
    const error = form.querySelector('[data-error]');
    if (error) error.hidden = false;

    const invalidFields = form.querySelectorAll(':invalid');

    invalidFields.forEach((field) => {
        const fieldName = field.name;
        if( fieldName ) {
            field.classList.add('form-input-error');
            switch (fieldName) {
            case 'email':
                field.value = '';
                field.placeholder='Please enter a valid email address';
                break;
            case 'first_name':
                field.placeholder='Please enter your first name';
                break;
            case 'dog_name':
                field.placeholder=`Please enter your dog's name`;
                break;
            }
        }
    });

    resetSubmitButton(form);
    
};

const validateRequiredCheckboxGroup = (form) => {
  const fieldsets = form.querySelectorAll('.js-checkbox-fieldset');
  let allValid = true;

  fieldsets.forEach((fieldset) => {
    const checkboxes = fieldset.querySelectorAll('.js-klaviyo-checkbox');
    const error = fieldset.querySelector('[data-heading-error]');
    const heading = fieldset.querySelector('[data-heading]');

    const isValid = Array.from(checkboxes).some((checkbox) => checkbox.checked);

    if (!isValid) {
        allValid = false;

        if (error) error.hidden = false;
        if (heading) heading.hidden = true;

        checkboxes[0]?.focus();

        checkboxes.forEach((checkbox) => {
        const label = checkbox.closest('.js-klaviyo-label');
        if (label) label.classList.add('form-checkbox-error');
        });
    } else {
        if (error) error.hidden = true;
        if (heading) heading.hidden = false;

        checkboxes.forEach((checkbox) => {
        const label = checkbox.closest('.js-klaviyo-label');
        if (label) label.classList.remove('form-checkbox-error');
        });

        resetSubmitButton(form);
    }
  });

  return allValid;
};

document.addEventListener('DOMContentLoaded', () => {

    // Form submission
    const klaviyoForms = document.querySelectorAll('.js-klaviyo-form');
    klaviyoForms.forEach((form) => {
        form.addEventListener('submit', async (event) => {
            event.preventDefault();

            const isNativeValid = form.checkValidity();
            const isCheckboxGroupValid = validateRequiredCheckboxGroup(form);

            if (!isNativeValid || !isCheckboxGroupValid) {
                console.error('Klaviyo form validation failed', {
                    isNativeValid,
                    isCheckboxGroupValid,
                    form
                });

                handleKlaviyoError(form);
                return;
            }

            await handleKlaviyoSubmission(form);
        });
    });


    // Custom checkbox select
    document.querySelectorAll('.js-klaviyo-checkbox').forEach((input) => {
    const label = input.closest('.js-klaviyo-label');

    if (!label) return;

    const updateState = () => {
        label.classList.toggle('checked', input.checked);
    };

    input.addEventListener('change', updateState);
        updateState();
    });

    // Pre-fill form inputs
    klaviyoForms.forEach((form) => {
        const prefill = form.dataset.prefill;

        if( prefill == "false" ) {
            return;
        }

        const fields = form.elements;
        const emailInput = fields.email;
        const firstNameInput = fields.first_name;
        const dogNameInput = fields.dog_name;

        let savedEmail = '';
        let savedFirstName = '';
        let savedDogName = '';

        try {
            savedEmail = localStorage.getItem('klaviyo_email') || '';
            savedFirstName = localStorage.getItem('klaviyo_first_name') || '';
            savedDogName = localStorage.getItem('klaviyo_dog_name') || '';
        } catch (storageError) {
            console.warn('Could not read Klaviyo form values:', storageError);
        }

        if (savedEmail && emailInput) {
            emailInput.value = savedEmail;
        }

        if (savedFirstName && firstNameInput) {
            firstNameInput.value = savedFirstName;
        }

        if (savedDogName && dogNameInput) {
            dogNameInput.value = savedDogName;
        }
    });

    // Remove error styles on input edit
    document.querySelectorAll('.js-klaviyo-form input').forEach((input) => {
        input.addEventListener('input', () => {
            input.classList.remove('form-input-error');
        });
    });
    
});